import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import { CALENDAR_FORMAT, formatDatePattern } from "common/calendar";
import validateFileSize from "common/validate-file-size";
import serveRequestErrors from "common/serve-request-errors";
import converReadableFileSize from "common/convert-readable-file-size";
import placeholderFormat from "common/placeholder-format";
import { getOvertimeDateRange, isOvertimeDateInRange, isEndTimeAfterStartTime, calculateOvertimeHours } from "common/overtime";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppCalendarInput from "components/app-calendar-input";
import AppTimeInput from "components/app-time-input";
import uploadIcon from "assets/images/upload-icon.svg";

const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

export const AppApplyOvertimeModal = (props, ref) => {
	const dispatch = useDispatch();
	const uploadInputRef = useRef();
	const [visible, setVisible] = useState(false);
	/* AppCalendarInput captures min/max into a ref on first render, so these must be stable */
	const dateRange = useMemo(() => getOvertimeDateRange(), []);
	const initialValues = useMemo(() => ({ overtimeDate: "", startTime: "", endTime: "", description: "", file: null }), []);
	//prettier-ignore
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			overtimeDate: yup.string().required(ERRORS.REQUIRED).test("withinRange", ERRORS.OVERTIME_DATE_RANGE, (value) => isOvertimeDateInRange(value)),
			startTime: yup.string().required(ERRORS.REQUIRED),
			endTime: yup.string().required(ERRORS.REQUIRED).test("afterStartTime", ERRORS.END_TIME_AFTER_START, function (value) { return isEndTimeAfterStartTime(this.parent.startTime, value); }),
			description: yup.string().required(ERRORS.REQUIRED),
			file: yup.mixed().nullable().test("fileSize", ERRORS.FILE_SIZE, validateFileSize),
		}),
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	const fileSize = useMemo(() => formik.values?.file?.size, [formik.values]);

	const overtimeHours = useMemo(() => calculateOvertimeHours(formik.values.startTime, formik.values.endTime), [formik.values.startTime, formik.values.endTime]);

	const onHandleShow = useCallback(() => {
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
	}, []);

	const onHandleUploadFile = useCallback(() => {
		uploadInputRef.current.click();
	}, []);

	//prettier-ignore
	const onHandleChange = useCallback((event) => {
		const file = event.target.files[0];
		formik.setFieldValue("file", file);
	}, [formik]);

	//prettier-ignore
	const onHandleUploadAttachment = useCallback(async (values, overtimeId) => {
		let response = false;

		if (values.file) {
			try {
				const formData = new FormData();
				formData.append("files", values.file);
				await api.post.overtime.uploadAttachment(overtimeId, formData);
				response = true;
			} catch (error) {
				serveRequestErrors(error);
				formik.setSubmitting(false);
			}
		}
		else {
			response = true;
		}

		if (response) {
			setVisible(false);
			formik.resetForm();
			dispatch(promptAlertMessage({ message: "Overtime has been submitted successfully" }));
			props.onHandleGetList();
		}
	}, [dispatch, formik, props]);

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		let response = false;

		try {
			const payload = {
				overtimeDate: formatDatePattern(values.overtimeDate, CALENDAR_FORMAT.CALENDAR_FORMAT),
				startTime: values.startTime,
				endTime: values.endTime,
				overtimeHours: overtimeHours,
				description: values.description,
			};
			response = await api.post.overtime.apply(payload);
		} catch (error) {
			serveRequestErrors(error);
			formik.setSubmitting(false);
		}

		if (response) onHandleUploadAttachment(values, response?.id);
	}, [formik, overtimeHours, onHandleUploadAttachment]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	return (
		<Modal classes={{ root: "app-apply-overtime-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="apply-overtime-modal" aria-describedby="apply-overtime-modal">
			<div className="apply-overtime-modal">
				<div className="apply-overtime-modal__main">
					<div className="apply-overtime-modal__header">
						<p className="apply-overtime-modal__title">New Overtime</p>
						<div className="apply-overtime-modal__note">
							Note:
							<ol>
								<li>Overtime can only be submitted for a past date within the last 1 month.</li>
								<li>Overtime must start and end on the same day.</li>
							</ol>
						</div>
					</div>

					<form className="apply-overtime-modal__body" onSubmit={formik.handleSubmit}>
						{/* prettier-ignore */}
						<AppCalendarInput required name="overtimeDate" label="Overtime Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={dateRange.minDate} maxDate={dateRange.maxDate} value={formik.values.overtimeDate} error={formik.errors.overtimeDate} touched={formik.touched.overtimeDate} disabled={formik.isSubmitting} onChange={formik.setFieldValue} />

						<AppInput type="text" label="Overtime Hours" value={overtimeHours ? overtimeHours.toString() : "-"} disabled />

						<div className="apply-overtime-modal__time-row">
							{/* prettier-ignore */}
							<AppTimeInput required name="startTime" label="Start Time" placeholder="--:--" value={formik.values.startTime} error={formik.errors.startTime} touched={formik.touched.startTime} disabled={formik.isSubmitting} onChange={formik.setFieldValue} />

							{/* prettier-ignore */}
							<AppTimeInput required name="endTime" label="End Time" placeholder="--:--" minTime={formik.values.startTime} value={formik.values.endTime} error={formik.errors.endTime} touched={formik.touched.endTime} disabled={formik.isSubmitting} onChange={formik.setFieldValue} />
						</div>

						{/* prettier-ignore */}
						<AppInput required multiline multilineRow={4} maxLength={500} type="text" name="description" label="Description" placeholder="Description" value={formik.values.description} error={formik.errors.description} touched={formik.touched.description} disabled={formik.isSubmitting} onChange={formik.handleChange} />

						<div className="apply-overtime-modal__upload-input">
							{/* prettier-ignore */}
							<label className="app-input__label" htmlFor="attachment">Attachment</label>
							<p className="apply-overtime-modal__description">Please ensure that your file is in the correct format (jpg, jpeg, png, pdf) with the file size not exceeding 5MB. Only 1 file can be uploaded.</p>

							<div className="apply-overtime-modal__input">
								<button type="button" className="apply-overtime-modal__upload-button" onClick={onHandleUploadFile}>
									{formik.values?.file?.name || "Upload"}
									<img className="apply-overtime-modal__icon" src={uploadIcon} alt="upload" />
								</button>

								<p className="apply-overtime-modal__label">{converReadableFileSize(fileSize, true) || "-"}</p>

								<input type="file" name="file" accept="image/jpeg, image/jpg, image/png, application/pdf" hidden ref={uploadInputRef} onChange={onHandleChange} />
							</div>

							{formik.touched.file && formik.errors.file && <div className="apply-overtime-modal__error">{formik.errors.file}</div>}
						</div>

						<div className="apply-overtime-modal__button-container">
							<AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
							<AppButton type="submit" label="Create" disabled={formik.isSubmitting} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppApplyOvertimeModal));

AppApplyOvertimeModal.propTypes = {
	ref: PropTypes.object,
	onHandleGetList: PropTypes.func,
};
